import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MasterService } from '../core/services/master.service';
import { UserRoleService } from '../core/services/user-role.service';
import { ReviewerService } from '../core/services/reviewer.service';
import { RebarAuthService } from '../core/rebarauth/rebar.auth.service';
import { IPeopleDetails, PeoplePickerComponent } from '../core/components/people-picker/people-picker.component';
import { DateFormatService } from '../core/services/date-format.service';
import { AuditLogGridComponent } from './audit-log/audit-log-grid.component';


interface Role {
  name: string;
  type: 'Base Role' | 'Custom Role';
  description: string;
  users: number;
  isCustom: boolean;
}

interface UserRecord {
  name: string;
  subtitle: string;
  email: string;
  roles: string[];
  country: string;
  status: 'Active' | 'Inactive';
  expiry: string | null;
  actAsReviewer: string;
  actAsSupervisor: string;
  actAsManager: string;
  requestedBy: string | null;
  createdDate: string | null;
  updatedDate: string | null;
}

type ActiveTab = 'roles' | 'users' | 'audit';
type FilterType = 'All' | 'Base Role' | 'Custom Role';

@Component({
  standalone: true,
  selector: 'app-admin',
  imports: [CommonModule, FormsModule, PeoplePickerComponent, RouterLink, AuditLogGridComponent],
  templateUrl: './admin.component.html',
  styleUrl: './admin.component.css',
})
export class AdminComponent implements OnInit {
  private static readonly roleNameMap: Record<string, string> = {
    Administrator: 'Admin / SuperUser',
  };

  private destroyRef      = inject(DestroyRef);
  private route           = inject(ActivatedRoute);
  private _activePicker: HTMLInputElement | null = null;
  private _pickerResizeHandler: (() => void) | null = null;
  private reviewerService = inject(ReviewerService);
  private dateFormatSvc   = inject(DateFormatService);

  formatDateDisplay(value: string | null | undefined): string {
    return this.dateFormatSvc.format(value);
  }

  openDatePicker(input: HTMLInputElement): void {
    if (!input.disabled) {
      this._activePicker = input;
      input?.showPicker?.();
    }
  }

  constructor(
    private masterService: MasterService,
    private userRoleService: UserRoleService,
    private router: Router,
    private rebarAuth: RebarAuthService
  ) {}

  isLoading = false;
  activeTab: ActiveTab = 'roles';
  searchQuery = '';
  activeFilter: FilterType = 'All';

  userList: UserRecord[] = [];
  userSearchQuery = '';
  selectedRoleFilter = 'All Roles';
  selectedStatusFilter = 'All Status';
  userCurrentPage = 1;
  readonly userPageSize = 10;
  designationOptionsForAdmin: string[] = [];
  userName: string;

  roles: Role[] = [
    {
      name: 'R&C Member',
      type: 'Base Role',
      description: 'All RNC staff. Broad view, search & download across all reviews, issues and action plans. No transactional access unless assigned to a specific review.',
      users: 0,
      isCustom: false,
    },
    {
      name: 'Admin / SuperUser',
      type: 'Base Role',
      description: 'Full system access. User provisioning, role management, master data & full system configuration. Full CRUD on all entities.',
      users: 0,
      isCustom: false,
    },
    {
      name: 'Regional Supervisor — EMEA',
      type: 'Custom Role',
      description: 'Supervisor role restricted to EMEA reviews only.',
      users: 0,
      isCustom: true,
    },
  ];

  ngOnInit(): void {
    this._pickerResizeHandler = () => {
      if (this._activePicker && !this._activePicker.disabled) {
        const picker = this._activePicker;
        picker.blur();
        setTimeout(() => picker.showPicker?.(), 0);
      }
    };
    window.addEventListener('resize', this._pickerResizeHandler);
    this.destroyRef.onDestroy(() => window.removeEventListener('resize', this._pickerResizeHandler!));

    if (!this.userRoleService.isAdmin) {
      this.router.navigate(['/admin/no-access']);
      return;
    }
    if (this.route.snapshot.queryParamMap.get('tab') === 'audit') {
      this.activeTab = 'audit';
    }
    this.loadUsers();
    this.masterService.getRoleNames().subscribe(res => {
      this.roleData = res;
      this.designationOptionsForAdmin = res.map((role: any) => role.Role_Name); });

    this.userName = this.rebarAuth.getUserName();
  }

  private loadUsers(): void {
     this.isLoading = true;
    this.masterService.getRoleDetails().subscribe((users: any[]) => {
      const counts: Record<string, number> = {};
      for (const user of users) {
        const apiName: string = user['Role_Name'] ?? '';
        const displayName = AdminComponent.roleNameMap[apiName] ?? apiName;
        counts[displayName] = (counts[displayName] ?? 0) + 1;
      }
      this.roles = this.roles.map((role) => ({
        ...role,
        users: counts[role.name] ?? 0,
      }));
      this.userList = users.map((u: any) => ({
        name: u['Name'] ?? u['User_Name'] ?? '',
        subtitle: `${u['Role_Name'] ?? ''}`,
        email: u['Email'] ?? u['EID'] ?? '',
        roles: u['Role_Name'] ? [u['Role_Name']] : [],
        country: u['Country'] ?? '',
        status: u['IsActive'] ? 'Active' : 'Inactive',
        expiry: u['ExpirationOn'] ?? null,
        actAsReviewer: String(u['ACT_AS_REVIEWER'] ?? '').trim() || '—',
        actAsSupervisor: String(u['ACT_AS_SUPERVISOR'] ?? '').trim() || '—',
        actAsManager: String(u['ACT_AS_MANAGER'] ?? '').trim() || '—',
        requestedBy: u['Created_By'] ?? u['CreatedBy'] ?? null,
        createdDate: u['Created_Date'] ?? u['CreatedDate'] ?? null,
        updatedDate: u['ModifiedDate'] ?? null,
      }));
      this.isLoading = false;
    });
  }

  get filteredRoles(): Role[] {
    return this.roles.filter((role) => {
      const matchesFilter =
        this.activeFilter === 'All' || role.type === this.activeFilter;
      const q = this.searchQuery.toLowerCase();
      const matchesSearch =
        !this.searchQuery ||
        role.name.toLowerCase().includes(q) ||
        role.description.toLowerCase().includes(q) ||
        role.type.toLowerCase().includes(q) ||
        role.users.toString().includes(q);
      return matchesFilter && matchesSearch;
    });
  }

  get baseRoleCount(): number {
    return this.roles.filter((r) => !r.isCustom).length;
  }

  get customRoleCount(): number {
    return this.roles.filter((r) => r.isCustom).length;
  }

  setTab(tab: ActiveTab): void {
    this.activeTab = tab;
  }

  setFilter(filter: FilterType): void {
    this.activeFilter = filter;
  }

  get filteredUsers(): UserRecord[] {
    return this.userList.filter(u => {
      const matchRole = this.selectedRoleFilter === 'All Roles' || u.roles.includes(this.selectedRoleFilter);
      const matchStatus = this.selectedStatusFilter === 'All Status' || u.status === this.selectedStatusFilter;
      const q = this.userSearchQuery.toLowerCase();
      const matchSearch = !q ||
        u.name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        u.roles.some(r => r.toLowerCase().includes(q)) ||
        u.country.toLowerCase().includes(q) ||
        u.status.toLowerCase().includes(q) ||
        (u.expiry ? this.formatDateDisplay(u.expiry).toLowerCase().includes(q) : false);
      return matchRole && matchStatus && matchSearch;
    });
  }

  get pagedUsers(): UserRecord[] {
    const start = (this.userCurrentPage - 1) * this.userPageSize;
    return this.filteredUsers.slice(start, start + this.userPageSize);
  }

  get userTotalPages(): number {
    return Math.max(1, Math.ceil(this.filteredUsers.length / this.userPageSize));
  }

  // ── Add New User panel ──
  showAddUserPanel   = false;
  showAddUserErrors  = false;
  addUserSubmitError = '';

  newUserEmail         = '';
  newUserFirstName     = '';
  newUserLastName      = '';
  newUserDesignation   = '';
  newUserCountry       = '';
  newUserCountryId: number | null = null;
  newUserWorkLocation  = '';
  newUserExpirationOn  = '';
  newUserStatus        = 'Active';
  newUserNotify        = '';
  newUserEidError      = '';
  newUserEidAutoFilled = false;
  private validEidMap  = new Map<string, { firstName: string; lastName: string }>();
  roleData: any[] = [];
  countryListForAdmin: { id: number; name: string }[] = [];
  workLocationListForAdmin: string[]                  = [];
  isLoadingCountriesForAdmin  = false;
  isLoadingLocationsForAdmin  = false;

  get todayDateStrAdmin(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  get addUserMandatoryFieldsValid(): boolean {
    return !!(
      this.newUserEmail.trim() &&
      this.newUserFirstName.trim() &&
      this.newUserLastName.trim() &&
      this.newUserDesignation &&
      this.newUserCountry &&
      this.newUserWorkLocation
    );
  }

  onAddNewUser(): void {
    this.showAddUserPanel  = true;
    this.showAddUserErrors = false;
    this.addUserSubmitError = '';
    this.newUserEmail = ''; this.newUserFirstName = ''; this.newUserLastName = '';
    this.newUserDesignation = ''; this.newUserCountry = ''; this.newUserCountryId = null;
    this.newUserWorkLocation = ''; this.newUserExpirationOn = ''; this.newUserStatus = 'Active';
    this.newUserNotify = ''; this.workLocationListForAdmin = [];
    this.newUserEidError = ''; this.newUserEidAutoFilled = false;
    if (!this.countryListForAdmin.length) { this.loadCountriesForAdmin(); }
    this.loadValidEids();
  }

  private loadCountriesForAdmin(): void {
    this.isLoadingCountriesForAdmin = true;
    this.reviewerService.getAllCountries()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any[]) => {
          this.countryListForAdmin = (res || [])
            .map(c => ({ id: c.CountryMasterID ?? c.ID ?? c.id, name: c.CountryName ?? c.Name ?? c.name ?? '' }))
            .filter(c => c.name);
          this.isLoadingCountriesForAdmin = false;
        },
        error: () => { this.isLoadingCountriesForAdmin = false; },
      });
  }

  onAdminCountryChange(): void {
    this.newUserWorkLocation = '';
    this.workLocationListForAdmin = [];
    const country = this.countryListForAdmin.find(c => c.name === this.newUserCountry);
    if (!country) return;
    this.newUserCountryId = country.id;
    this.isLoadingLocationsForAdmin = true;
    this.reviewerService.getAllCities(country.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any[]) => {
          this.workLocationListForAdmin = (res || []).map(ci => ci.CityName ?? ci.Name ?? ci.name ?? '').filter(Boolean);
          this.isLoadingLocationsForAdmin = false;
        },
        error: () => { this.isLoadingLocationsForAdmin = false; },
      });
  }

  onAddUserCancel(): void {
    this.showAddUserPanel = false;
  }

  onPersonSelected(person: IPeopleDetails): void {
    this.newUserFirstName     = person.FirstName ?? '';
    this.newUserLastName      = person.LastName  ?? '';
    this.newUserEidError      = '';
    this.newUserEidAutoFilled = true;
  }

  private loadValidEids(): void {
    this.validEidMap.clear();
    this.reviewerService.getUsersInformation('').subscribe({
      next: (res: any) => {
        const list: any[] = Array.isArray(res) ? res : (res?.value ?? res?.recordset ?? []);
        list.forEach((u: any) => {
          const raw = u.EnterpriseId ?? u.enterpriseId ?? u.EID ?? u.eid ?? u.Email ?? u.email ?? '';
          const eid = (raw.includes('@') ? raw.split('@')[0] : raw).toLowerCase().trim();
          if (eid) this.validEidMap.set(eid, {
            firstName: u.FirstName ?? u.firstName ?? '',
            lastName:  u.LastName  ?? u.lastName  ?? '',
          });
        });
      },
      error: () => {},
    });
  }

  onNewUserEmailInput(): void {
    this.newUserEidError      = '';
    this.newUserEidAutoFilled = false;
    this.newUserFirstName     = '';
    this.newUserLastName      = '';
  }

  validateNewUserEid(): void {
    if (this.newUserEidAutoFilled) return;
    const raw = this.newUserEmail.trim();
    if (!raw || this.validEidMap.size === 0) return;
    const eid = (raw.includes('@') ? raw.split('@')[0] : raw).toLowerCase();
    const person = this.validEidMap.get(eid);
    if (person) {
      this.newUserEidError      = 'Users already active in AST.';
      this.newUserEidAutoFilled = false;
    } else {
      this.newUserEidError      = 'Entered EID is not valid. Please enter a valid EID.';
      this.newUserEidAutoFilled = false;
    }
  }

  onAddUserSubmit(): void {
    this.showAddUserErrors = true;
    this.validateNewUserEid();
    if (!this.addUserMandatoryFieldsValid) {
      this.addUserSubmitError = 'Please fill in all mandatory fields before submitting.';
      return;
    }
    if (this.newUserEidError) {
      this.addUserSubmitError = '';
      return;
    }
    this.addUserSubmitError = '';
    // API
    const selectedRoleId = this.roleData.find(x => x.Role_Name === this.newUserDesignation)?.Role_ID;
    const params = {
          category: "InsertUser",
          UserID: null,
          Email: this.newUserEmail,
          FirstName: this.newUserFirstName,
          LastName: this.newUserLastName,
          EnterpriseID: this.newUserEmail.replace('@accenture.com', ''),
          RoleID: selectedRoleId,
          Country: this.newUserCountry,
          WorkLocation: this.newUserWorkLocation,
          ExpirationOn: this.newUserExpirationOn || null,
          ReasonForReject: '',
          IsActive: this.newUserStatus === 'Active' ? 1 : 0,
          LoggedUserID: this.userName
        };
        // API Call
        this.isLoading = true;
        this.masterService.insertNewUser(params)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (res: any) => {
              this.isLoading = false;
              const items = Array.isArray(res) ? res : (res ? [res] : []);
              const apiMessage = items[0]?.Message ?? '';
              if (apiMessage === 'Users already active in AST.') {
                this.newUserEidError = apiMessage;
                return;
              }
              this.activeTab = 'users';
              this.showAddUserPanel = false;
              this.loadUsers();
            },
            error: (err) => {
              console.error('New User Insertion error', err);
              this.isLoading = false;
              alert(err);
            },
          });
        }

  onViewUser(user: UserRecord): void {
    // Placeholder — wire to a dialog/form in a future iteration
    console.log(`View user: ${user.name}`);
  }

  onEditUser(user: UserRecord): void {
    console.log(`Edit user: ${user.name}`);
  }

  onDeleteUser(user: UserRecord): void {
    console.log(`Delete user "${user.name}"?`);
    }

  onAddNewRole(): void {
    // Placeholder — wire to a dialog/form in a future iteration
    console.log('Add New Role dialog — coming soon.');
  }

  onView(role: Role): void {
    this.router.navigate(['/admin/view-role-permissions'], {
      queryParams: { roleName: role.name, roleType: role.type }
    });
  }

  onEdit(role: Role): void {
    console.log(`Editing role: ${role.name}`);
  }

  onDelete(role: Role): void {
    console.log(`Delete role "${role.name}"?`);
    }
}
